'use client';

import { type NodeProps, type Node } from '@xyflow/react';
import { Text } from '@terra/ui-design-system';
import { Box } from '@terra/ui-styled-system';
import { Layers01Icon } from '@terra/ui-icons';
import type { AiFlowNodeCategory } from './types';
import {
  getAiFlowNodeColor,
  getAiFlowNodeIcon,
  getAiFlowNodeLabel,
  isCodeGraphEligible,
} from './utils';
import { BaseGraphNode } from '../flow-graph-base/BaseGraphNode';

export type AiFlowCustomNodeProps = {
  type: AiFlowNodeCategory;
  name: string;
  filePath: string;
  startLine?: number | null;
  endLine?: number | null;
  repositoryName: string;
  id: string;
  hasCodeGraph: boolean;
  framework?: string | null;
};
export function AiFlowCustomNode({
  data: { type, name, hasCodeGraph, id },
}: NodeProps<Node<AiFlowCustomNodeProps>>) {
  const NodeIcon = getAiFlowNodeIcon(type);
  const nodeColor = getAiFlowNodeColor(type);
  const eligible = isCodeGraphEligible(type);
  const iconBadge =
    eligible && hasCodeGraph ? (
      <Box
        position="absolute"
        bottom="-2px"
        right="-2px"
        w="18px"
        h="18px"
        borderRadius="full"
        bg="bg.default"
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderWidth="1px"
        borderColor="border.default"
      >
        <Layers01Icon size={10} color="var(--colors-fg-muted)" />
      </Box>
    ) : undefined;
  const typeBadge = (
    <Box
      mb={1.5}
      px={1.5}
      py={0.5}
      borderRadius="full"
      style={{
        backgroundColor: `color-mix(in srgb, ${nodeColor} 18%, transparent)`,
        border: `1px solid color-mix(in srgb, ${nodeColor} 35%, transparent)`,
      }}
    >
      <Text
        fontSize="2xs"
        fontWeight={600}
        style={{
          color: nodeColor,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        {getAiFlowNodeLabel(type)}
      </Text>
    </Box>
  );

  return (
    <BaseGraphNode
      id={id}
      name={name}
      nodeColor={nodeColor}
      NodeIcon={NodeIcon}
      cursor={eligible ? 'pointer' : 'default'}
      iconBadge={iconBadge}
      aboveName={typeBadge}
    />
  );
}
